// @flow
import * as React from 'react';
// $FlowIssue
import { useReducer, useRef } from 'react';
import { ArticleContainer } from './QAComponents';
import {
  useMediaUpload,
  DEFAULT_MAX_MEDIA_ITEMS,
  CaptionTextarea,
  MediaUploadControl,
  MediaThumbnail,
  serializeResponseMedia,
} from './MediaUpload';
import { useAuth, withAuthError } from 'ducks/auth-context';
import { postResponse as _postResponse } from 'ducks/coreDataApi';
import { Alert, ErrorAlert } from 'components/alerts';
import { Ugcs } from 'models';
import ClickOutside from 'react-click-outside';
import classnames from 'classnames';
import { AnswerTextarea } from './AnswerTextarea';
import { nanoid } from 'lib/nanoid';

const postResponse = withAuthError(_postResponse);

type Props = {
  questionId: Id,
  allowMedia: boolean,
  subject: Product,
  locale: string,
  onSubmit: Ugc => void,
};

function AddAnswerMain(props: Props) {
  const { questionId, allowMedia = true, subject, locale, onSubmit } = props;
  const { token } = useAuth();
  const [{ isLoading, answerText, error, isExpanded }, dispatch] = useReducer(
    reducer,
    initialState()
  );
  const {
    media,
    captions,
    canPersistMedia,
    addFiles,
    uploadFile,
    setCaption,
    removeFile,
    clearFiles,
    reset,
    isModalOpen,
    toggleModal,
    maxItems,
  } = useMediaUpload(DEFAULT_MAX_MEDIA_ITEMS);
  const isOverMediaLimit = media.size > DEFAULT_MAX_MEDIA_ITEMS;
  const answerIsEmpty = Boolean(answerText.trim()) === false;

  async function submitAnswer() {
    dispatch({
      type: 'submit',
    });
    try {
      const data = {
        parent_ugc_id: questionId,
        subject_id: subject.subject_id,
        catalog_id: subject.merchant.id,
        locale: locale,
        ugc_type: 'Answer',
        text: answerText,
        answer_media: serializeResponseMedia(media, captions),
      };
      const response = await postResponse(token, data);
      onSubmit(Ugcs.createWithData({ ugc: response }));
      reset();
      dispatch({ type: 'submit/success' });
    } catch (err) {
      console.log(err);
      const errMessage =
        err.status === 403
          ? 'You do not have the necessary permissions to make this change.'
          : 'An error occurred while posting your response.';
      dispatch({ type: 'submit/fail', payload: errMessage });
    }
  }

  const idRef = useRef(nanoid());
  function id(strings: string[]) {
    return `${idRef.current}-${strings.join('-')}`;
  }
  const answerControlsContainerId = id`answer-controls`;

  return (
    <ClickOutside
      onClickOutside={() =>
        dispatch({
          type: 'blur',
          payload: media.size === 0 && answerIsEmpty && !isModalOpen,
        })
      }
      style={{
        opacity: isLoading ? 0.5 : 1,
        pointerEvents: isLoading ? 'none' : 'auto',
      }}
    >
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {isOverMediaLimit ? (
        <Alert
        >{`You can only include ${DEFAULT_MAX_MEDIA_ITEMS} media items.`}</Alert>
      ) : null}
      <AnswerTextarea
        minRows={isExpanded ? 2 : 1}
        id={id`answer-textarea`}
        aria-controls={answerControlsContainerId}
        value={answerText}
        onChange={e => dispatch({ type: 'answer', payload: e.target.value })}
        placeholder="Add an answer to this question"
        onFocus={() => dispatch({ type: 'focus' })}
      />
      <ul className="list mv4 pa0">
        {Array.from(media.entries()).map(([id, item]: [string, *]) => {
          return (
            <li key={id} className="flex items-start mb4">
              <MediaThumbnail item={item} onDelete={removeFile} />
              <CaptionTextarea
                placeholder="Add a caption..."
                value={captions.get(id) || ''}
                onChange={e => {
                  setCaption(id, e.target.value);
                }}
              />
            </li>
          );
        })}
      </ul>
      <div
        style={{ display: isExpanded ? 'flex' : 'none' }}
        id={answerControlsContainerId}
        aria-expanded={isExpanded}
      >
        {allowMedia && (
          <MediaUploadControl
            media={media}
            isOpen={isModalOpen}
            onToggle={toggleModal}
            onUpload={uploadFile}
            onDelete={removeFile}
            maxItems={maxItems}
            onAdd={addFiles}
            id={id`media-input`}
          />
        )}

        <div style={{ marginLeft: 'auto' }}>
          <button
            className="btn btn--small btn--link"
            onClick={() => {
              clearFiles();
              dispatch({ type: 'cancel' });
            }}
          >
            Cancel
          </button>
          <button
            className={classnames('btn btn--small btn--primary', {
              'btn--loading': isLoading,
            })}
            type="submit"
            onClick={submitAnswer}
            disabled={answerIsEmpty || !canPersistMedia || isOverMediaLimit}
          >
            Submit
          </button>
        </div>
      </div>
    </ClickOutside>
  );
}

function initialState() {
  return {
    answerText: '',
    isLoading: false,
    error: null,
    isExpanded: false,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'focus':
      return { ...state, isExpanded: true };
    case 'blur': {
      const collapse = action.payload;
      return collapse ? { ...state, isExpanded: false } : state;
    }
    case 'answer': {
      return {
        ...state,
        answerText: action.payload,
      };
    }
    case 'submit': {
      return {
        ...state,
        isLoading: true,
      };
    }
    case 'submit/success':
      return initialState();
    case 'submit/fail':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case 'cancel':
      return initialState();

    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

export function AddAnswer(props: Props) {
  return (
    <ArticleContainer ugctype="Answer" main={<AddAnswerMain {...props} />} />
  );
}



// WEBPACK FOOTER //
// ./src/components/ContentSection/__future__/AddAnswer.js